import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { createResourceStream } from '../../../services/UnlimitedService';
import { makeBookId } from '../../Homepage/Contexts/DiffitBooksContext';
import { Mixpanel } from '../../../services/Mixpanel';
import { buildCurriculumPayload, mergeCurriculumStandards } from '../../../utils/curriculum';
import { buildAdaptParams } from '../utils/buildAdaptParams';
import { trackGAEvent } from '../../../services/GoogleAnalytics';
import { trackMetaCustomEvent } from '../../../services/MetaPixel';
import { trackChatGPTCustomEvent } from '../../../services/ChatGPTPixel';
import { useLatestRef } from '../../../hooks/useLatestRef';
import { isRateLimited } from '../../../utils/isRateLimited';
import {
  isProjectQuotaExceeded,
  withProjectCounted,
  withProjectQuotaExhausted,
} from '../../../utils/isProjectQuotaExceeded';
import { useError } from '../../../contexts/ErrorContext';
import {
  stashPendingGeneration,
  takePendingGeneration,
  clearPendingGeneration,
} from '../../../utils/pendingGeneration';
import { useSignInModal } from '../../../contexts/SignInModalContext';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';

/**
 * Handles the final resource creation step — builds the API payload, sends it to the
 * streaming create endpoint, and navigates to the result on success.
 *
 * @param {Object} params
 * @param {Function} params.getQuery - Returns the current query text.
 * @param {Function} params.setError - Setter to display error messages.
 * @param {Object|null} params.selectedBook - Currently selected book, if any.
 * @param {number} params.startChapter - Start chapter index for a selected book.
 * @param {number} params.endChapter - End chapter index for a selected book.
 * @param {Object|null} params.selectedTemplate - Template chosen by the user.
 * @param {Object[]} params.languageStandards - Selected language standards.
 * @param {Object[]} params.contentStandards - Selected content standards.
 * @param {Object|null} [params.curriculumSelection] - Align → Curriculum selection
 *   ({ curriculumId, courseName, lessons }). The lessons' identity is sent as
 *   curriculum_json, and the standards they teach are merged into
 *   content_standards_json — they were published alongside the lessons.
 * @param {Object[]} params.selectedReadingLevels - Target reading levels.
 * @param {Object[]} params.selectedLanguages - Target output languages.
 * @param {Object[]} params.imageAnalyses - Analysis results for uploaded images.
 * @param {any} params.sourceContent - Extracted source content (from PDF, URL, etc.).
 * @param {Function} params.getUnlimitedTrackingContext - Returns Mixpanel tracking context.
 * @param {string} params.savedRequest - The saved request text from submission.
 * @param {('tier_2'|'tier_3'|null)} [params.selectedMtssTier] - Optional MTSS tier overlay.
 * @param {('auto'|'on'|'off')} [params.originalTextMode] - Original-text handling. Sent as
 *   keep_original_text: true ('on') / false ('off'); 'auto' is omitted from the payload so
 *   the backend defaults to deciding itself.
 * @param {string|null} [params.pastedSourceText] - Large text pasted into the prompt, sent as a
 *   structured source when originalTextMode is 'on' and the paste is still present in the request.
 * @param {('auto'|'on'|'off')} [params.webSourcesMode] - Web-search tool exposure mode.
 *   Sent only when non-default ('on'/'off'); 'auto' is omitted from the payload so the
 *   backend defaults to the existing LLM-decides behavior.
 * @param {string|null} [params.compareExperimentId] - Admin debug mode: generate under both
 *   arms of this experiment and land in the eval tool's compare view instead of the packet.
 * @returns {{ isCreating: boolean, handleCreate: Function }}
 */
export const useResourceCreation = ({
  getQuery,
  setError,
  selectedBook,
  startChapter,
  endChapter,
  selectedTemplate,
  languageStandards,
  contentStandards,
  selectedReadingLevels,
  selectedLanguages,
  imageAnalyses,
  sourceContent,
  getUnlimitedTrackingContext,
  savedRequest,
  curriculumSelection = null,
  selectedMtssTier = null,
  originalTextMode = 'auto',
  pastedSourceText = null,
  webSourcesMode = 'auto',
  compareExperimentId = null,
}) => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();
  const { diffitUser, setDiffitUser } = useDiffitUserContext();
  const showSignInModal = useSignInModal();
  const { showProjectQuotaError } = useError();
  const [isCreating, setIsCreating] = useState(false);
  const abortControllerRef = useRef(null);
  // When false, the unmount cleanup leaves the in-flight stream alone. Only the
  // resume path clears it: the stash is consumed destructively, so React
  // StrictMode's dev mount→unmount→remount would abort the resumed generation on
  // the first cleanup and find nothing left to restart on the second mount.
  const abortOnUnmountRef = useRef(true);

  useEffect(() => {
    return () => {
      if (abortOnUnmountRef.current) abortControllerRef.current?.abort();
    };
  }, []);

  // Sends a fully-assembled create payload to the backend and navigates to the
  // result. Split out from handleCreate so the payload can be sent without
  // rebuilding it from component state.
  const runCreate = useCallback(
    async (createParams, { followUpContext = [], onFinally } = {}) => {
      setIsCreating(true);
      setError(null);

      const bookId = createParams.book_id || null;

      let didRedirect = false;
      // A project exists once these redirects fire; the comparison redirect makes none.
      let createdProject = false;
      try {
        let streamId = null;
        let lastEventId = null;
        let packetId = null;
        let redirectUrl = null;
        let totalActivities = 0;
        let redirected = false;
        // Orientation summary of the initial resource set, captured from
        // outline_ready and reused on the redirect/success events.
        let resourceOrientation = {};
        // Input-classifier tags (e.g. 'math'), captured from outline_ready
        // and attached to the redirect/success events.
        let classifierTags = [];
        // GA activation event. The backend logs the first generation once in
        // seen_nux_json (claimed/shared packets don't count) and stamps
        // is_first_resource on outline_ready AND done, so fire on whichever
        // arrives first — exactly once. Absent for anonymous creates.
        let firedFirstResource = false;
        const fireFirstResourceEvent = (source) => {
          if (firedFirstResource) return;
          firedFirstResource = true;
          trackGAEvent('first_unlimited_resource_generated', {
            primary_orientation: source.primary_orientation,
            num_pages_resources: source.num_pages_resources,
            num_slides_resources: source.num_slides_resources,
            has_book: !!bookId,
            reading_level_id: createParams.reading_level_id,
            language: createParams.language,
          });
          // First-ever resource for this teacher — the "tried Diffit" signal,
          // which no other event in the funnel carries: ViewContent fires on
          // every resource view, not just the first. Inside the dedupe guard
          // above, so the outline_ready/done double-fire can't double-count it.
          trackMetaCustomEvent('FirstResourceGenerated');
          trackChatGPTCustomEvent('first_resource_generated');
        };
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        await createResourceStream(
          createParams,
          (event) => {
            if (event.type === 'comparison_started') {
              didRedirect = true;
              navigate(`/eval?tab=compare&id=${event.comparison_id}`);
            } else if (event.type === 'stream_started') {
              streamId = event.stream_id;
            } else if (event.type === 'outline_ready') {
              packetId = event.packet_id;
              redirectUrl = event.redirect_url;
              totalActivities = event.activities?.length || 0;
              resourceOrientation = {
                primary_orientation: event.primary_orientation,
                num_pages_resources: event.num_pages_resources,
                num_slides_resources: event.num_slides_resources,
              };
              classifierTags = event.classifier_tags || [];
              if (event.is_first_resource) {
                fireFirstResourceEvent(event);
              }
              const sid = event.stream_id || streamId;
              try {
                if (event.outline) {
                  sessionStorage.setItem(`stream_outline_${sid}`, JSON.stringify(event.outline));
                }
              } catch {
                // sessionStorage may be unavailable
              }

              // Redirect immediately on outline_ready — don't wait for first activity
              if (!redirected) {
                redirected = true;
                const streamParams = new URLSearchParams({
                  stream_id: sid,
                  last_event_id: lastEventId || '0',
                  total_activities: String(totalActivities),
                });
                Mixpanel.track('Unlimited Create Streaming Redirect', {
                  packet_id: packetId,
                  has_book: !!bookId,
                  has_template: !!createParams.from_template_id,
                  follow_up_context: Array.isArray(followUpContext) ? followUpContext : [],
                  classifier_tags: classifierTags,
                  ...getUnlimitedTrackingContext(),
                  ...resourceOrientation,
                });
                didRedirect = true;
                createdProject = true;
                abortController.abort();
                navigate(`${redirectUrl}?${streamParams.toString()}`);
              }
            } else if (event.type === 'done' && !redirected) {
              if (event.redirect_url) {
                didRedirect = true;
                createdProject = true;
                if (event.is_first_resource) {
                  fireFirstResourceEvent(event);
                }
                Mixpanel.track('Unlimited Create Success', {
                  redirect_url: event.redirect_url,
                  packet_id: event.packet_id,
                  has_book: !!bookId,
                  has_template: !!createParams.from_template_id,
                  book_id: bookId,
                  follow_up_context: Array.isArray(followUpContext) ? followUpContext : [],
                  classifier_tags: classifierTags,
                  ...getUnlimitedTrackingContext(),
                  ...resourceOrientation,
                });
                navigate(event.redirect_url);
              }
            } else if (event.type === 'error' && !redirected) {
              throw new Error(event.message || 'Creation failed');
            }
          },
          abortController.signal
        );
      } catch (err) {
        if (err.name === 'AbortError') return;

        // Anonymous per-IP create cap. Signing in lifts it, so this isn't an
        // error the user should see: stash the exact payload, open the sign-in
        // modal, and let the resume effect below re-run it after SignInModal's
        // post-auth page reload. Gated on a *settled* signed-out session so a 429
        // that somehow reaches a signed-in user — or one whose session is still
        // hydrating, where isSignedIn is undefined — falls through to the normal
        // error path instead of prompting a sign-in they don't need.
        if (isRateLimited(err) && isLoaded && !isSignedIn) {
          const stashed = stashPendingGeneration({ createParams, followUpContext });
          Mixpanel.track('Unlimited Create Rate Limited', {
            transport: 'stream',
            retry_after_seconds: err.response?.data?.retry_after_seconds,
            stashed,
            ...getUnlimitedTrackingContext(),
          });
          showSignInModal({
            headline: 'Sign in to continue',
            // Dismissing shows nothing and discards the stash, so a user who
            // deliberately backed out never gets a surprise generation later.
            onClose: clearPendingGeneration,
          });
          // `finally` still runs — clears isCreating and fires onFinally, so a
          // mid-Q&A limit lands on the main view with the modal up.
          return;
        }

        // The stream request throws a fetch error the global handler never sees, so show the
        // quota modal here instead of a generic inline error.
        if (isProjectQuotaExceeded(err)) {
          showProjectQuotaError(err.response?.data);
          setDiffitUser((user) => withProjectQuotaExhausted(user, err.response?.data));
          return;
        }

        console.error('Error creating resource:', err);
        Mixpanel.track('Unlimited Create Failed', {
          error: err.message,
          has_template: !!createParams.from_template_id,
          ...getUnlimitedTrackingContext(),
        });
        // A rejected comparison (e.g. the experiment no longer differs from what's served)
        // says why; only admins in debug mode can send `compare`.
        setError(
          (createParams.compare && err.response?.data?.message) ||
            'Something went wrong. Please try again.'
        );
      } finally {
        abortControllerRef.current = null;
        if (createdProject) setDiffitUser(withProjectCounted);
        if (!didRedirect) {
          setIsCreating(false);
          onFinally?.();
        }
      }
    },
    [
      navigate,
      getUnlimitedTrackingContext,
      setError,
      isLoaded,
      isSignedIn,
      showSignInModal,
      showProjectQuotaError,
      setDiffitUser,
    ]
  );

  // Resume a generation that was rate limited before the user signed in.
  // Must call runCreate, not handleCreate: handleCreate rebuilds the payload
  // from component state, all of which is empty after SignInModal's reload.
  // runCreate's identity churns on every keystroke, so it rides a ref rather
  // than the dep array.
  const runCreateRef = useLatestRef(runCreate);
  const resumedRef = useRef(false);

  // Gate on diffitUser?.id, not just Clerk's isSignedIn: the backend user is
  // created by base.jsx's addUser AFTER the reload, and that POST is the only
  // thing that creates one. Resuming on isSignedIn alone races it, and a create
  // that wins the race is attributed anonymously — so it 429s again, this time
  // with isSignedIn true, landing on the generic error with the stash spent.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !diffitUser?.id || resumedRef.current) return;
    // Reads and clears in one synchronous step, before any await — this is what
    // stops a StrictMode double-mount from firing the same generation twice.
    const pending = takePendingGeneration();
    if (!pending) return;
    resumedRef.current = true;
    // The stash is gone now, so StrictMode's dev cleanup must not abort what it
    // started — nothing would be left to restart on the remount.
    abortOnUnmountRef.current = false;
    Mixpanel.track('Unlimited Create Resumed After Sign In', {
      stash_age_ms: Date.now() - pending.createdAt,
    });
    runCreateRef.current(pending.createParams, { followUpContext: pending.followUpContext });
  }, [isLoaded, isSignedIn, diffitUser?.id, runCreateRef]);

  const handleCreate = useCallback(
    async (
      followUpContext,
      passedSourceContent = null,
      { onFinally, generationSessionId } = {}
    ) => {
      setIsCreating(true);
      setError(null);

      const requestText = savedRequest || getQuery().trim();

      let bookId = null;
      let bookStartChapterIdx = null;
      let bookEndChapterIdx = null;

      let filteredFollowUpContext = followUpContext;
      if (Array.isArray(followUpContext)) {
        const bookAnswer = followUpContext.find((a) => a.type === 'book_selection');
        if (bookAnswer) {
          bookId = bookAnswer.book_id;
          bookStartChapterIdx = bookAnswer.start_chapter_idx;
          bookEndChapterIdx = bookAnswer.end_chapter_idx;
          filteredFollowUpContext = followUpContext.filter((a) => a.type !== 'book_selection');
        }
      }

      if (!bookId && selectedBook) {
        bookId = makeBookId(selectedBook);
        bookStartChapterIdx = startChapter;
        bookEndChapterIdx = endChapter;
      }

      let effectiveSourceContent = passedSourceContent || sourceContent;

      // When preserving original text, a large paste rides along as a structured
      // source — but only if it's still present (unedited) in the request, so a
      // stale capture never sends text the user removed or changed.
      if (
        originalTextMode === 'on' &&
        pastedSourceText &&
        requestText.includes(pastedSourceText) &&
        !(effectiveSourceContent?.texts || []).includes(pastedSourceText)
      ) {
        effectiveSourceContent = {
          ...(effectiveSourceContent || {}),
          texts: [...(effectiveSourceContent?.texts || []), pastedSourceText],
        };
      }

      Mixpanel.track('Unlimited Create Started', {
        // A skipped format question rides along with no answers; it is not context.
        has_follow_up_context: Array.isArray(filteredFollowUpContext)
          ? filteredFollowUpContext.some((qa) => qa.answers?.length !== 0)
          : Object.keys(filteredFollowUpContext).length > 0,
        has_source_content: !!effectiveSourceContent,
        has_book: !!bookId,
        has_template: !!selectedTemplate,
        has_standards: languageStandards.length > 0 || contentStandards.length > 0,
        has_curriculum: !!curriculumSelection?.lessons?.length,
        curriculum_id: curriculumSelection?.curriculumId,
        curriculum_source: curriculumSelection?.curriculumSource,
        ...getUnlimitedTrackingContext(),
      });

      // The selected lessons teach standards of their own (published with the
      // lesson); they join the teacher's picks, deduped in favour of the teacher's.
      const contentStandardsWithCurriculum = mergeCurriculumStandards(
        contentStandards,
        curriculumSelection
      );

      const createParams = {
        request: requestText,
        ...buildAdaptParams({
          selectedReadingLevels,
          selectedLanguages,
          selectedMtssTier,
          originalTextMode,
          webSourcesMode,
        }),
        orientation: selectedTemplate?.orientation || undefined,
        follow_up_context: filteredFollowUpContext,
        source_content: effectiveSourceContent || undefined,
        book_id: bookId || undefined,
        start_chapter_idx: bookStartChapterIdx !== null ? bookStartChapterIdx : undefined,
        end_chapter_idx: bookEndChapterIdx !== null ? bookEndChapterIdx : undefined,
        from_template_id: selectedTemplate?.id || undefined,
        language_standards_json: languageStandards.length > 0 ? languageStandards : undefined,
        content_standards_json:
          contentStandardsWithCurriculum.length > 0 ? contentStandardsWithCurriculum : undefined,
        image_analyses: imageAnalyses.length > 0 ? imageAnalyses : undefined,
        generation_session_id: generationSessionId || undefined,
        curriculum_json: buildCurriculumPayload(curriculumSelection),
        // Comparisons don't apply templates, so a template create never runs as one.
        ...(compareExperimentId && !selectedTemplate
          ? { compare: { experiment_id: compareExperimentId } }
          : {}),
      };

      await runCreate(createParams, { followUpContext: filteredFollowUpContext, onFinally });
    },
    [
      savedRequest,
      getQuery,
      selectedBook,
      startChapter,
      endChapter,
      selectedTemplate,
      languageStandards,
      contentStandards,
      selectedReadingLevels,
      selectedLanguages,
      imageAnalyses,
      sourceContent,
      getUnlimitedTrackingContext,
      curriculumSelection,
      selectedMtssTier,
      originalTextMode,
      pastedSourceText,
      webSourcesMode,
      compareExperimentId,
      runCreate,
    ]
  );

  return { isCreating, handleCreate };
};
